import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type AlertFormSchema, alertFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { AlertForm } from "@/forms/alert-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditAlertModalProps extends ModalProps {
	alertId: string;
	onComplete?: () => Promise<void>;
}

export function EditAlertModal({ isOpen, onClose, alertId, onComplete }: EditAlertModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.organizations.alerts[":alertId"].$get({
			param: { alertId },
		});
		const data = await responseHandler(response);
		return data.alertForm;
	});
	const methods = useForm({
		resolver: resolver(alertFormSchema),
		defaultValues,
	});

	/** Fucntions */
	async function handleOnSubmit(json: AlertFormSchema) {
		const response = await client.organizations.alerts[":alertId"].$patch({
			param: { alertId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("dashboard.alerts.edit_alert")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<AlertForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
